import { useMemo, useState } from "react";
import { useUserContext } from "../../../../../contexts/UserContext";
import { useRefetchRowDataList } from "../../../../../hooks";
import {
  RowData,
  RowDataReducerAction,
  RowDataReducerState,
  SaveRecordRequest,
} from "../../../../../types";
import {
  processBulkUpload,
  resetFileUploadData,
  validateBulkUploadRecords,
} from "../../../helpers";
import { useFileUpload } from "../../table-actions/FileUpload/useFileUpload";
import FileUploadDialog from "../../table-actions/FileUpload/FileUploadDialog";
import { DEFAULT_TOAST, TOAST_MESSAGES } from "../../../constants";
import { UploadState } from "../../table-actions/FileUpload/constants";
import {
  getCorelationFieldsColDefs,
  parseCorelationExcelFile,
} from "../../../helpers/importRjeReference";
import { downloadMergedSheetUploadTemplate } from "../../../helpers/importPricingMatrix/mergedSheetTemplateDownload";
import { getChildCollections } from "../../../helpers/export/schemaInspector";
import { exportMappedMergedRowsToExcel } from "../../../helpers/importPricingMatrix/exportedFlattenedMergedRows";
import {
  getImportStatusCounts,
  getImportToastMessage,
} from "../../../helpers/importRecordsHelpers";

interface PricingMatrixFileUploadProps {
  isOpen: boolean;
  onClose: () => void;
  rowDataReducerState: RowDataReducerState;
  dispatch: React.Dispatch<RowDataReducerAction>;
}

export default function PricingMatrixFileUpload({
  isOpen,
  onClose,
  rowDataReducerState,
  dispatch,
}: Readonly<PricingMatrixFileUploadProps>): JSX.Element {
  const { schema } = rowDataReducerState;
  const { selected, setupBaseRequestInfo, iamUserState } = useUserContext();
  const [uploadedData, setUploadedData] = useState<RowData[]>([]);
  const setupName = selected.setup.label;
  const [shouldRefetch, setShouldRefetch] = useState(false);
  const childCollections = getChildCollections(schema);
  const childCollection = childCollections[0];
  const corelationFieldProperties = childCollection?.properties;

  const { refetch: refetchRowDataList } = useRefetchRowDataList({
    setupBaseRequestInfo,
    headerLabel: undefined,
    dispatch,
    setTableTitleInfo: undefined,
  });

  const handleParseFile = async (file: File) => {
    // for parent and child merged setups parsing is the
    // same as parsing correlation if the all the parent fields are included in the Identifiers
    return parseCorelationExcelFile(
      file,
      schema,
      selected.setup.label,
      corelationFieldProperties,
      iamUserState,
    );
  };

  const handleValidateFile = async (data: RowData[]) => {
    const request: SaveRecordRequest = {
      ...setupBaseRequestInfo,
      record: data,
    };
    return validateBulkUploadRecords(request);
  };

  const {
    uploadState,
    validatedData,
    setUploadState,
    attachedFile,
    toastMessage,
    hasErrors,
    handleFileChange,
    removeFile,
    handleValidate,
    resetUpload,
    setToastMessage,
  } = useFileUpload({
    parseFile: handleParseFile,
    validateFile: handleValidateFile,
    customValidationToastMessage: TOAST_MESSAGES.OVERRIDE_WARNING(),
  });

  const handleImport = async () => {
    setToastMessage(DEFAULT_TOAST);
    setUploadState(UploadState.IMPORTING);
    try {
      const request: SaveRecordRequest = {
        ...setupBaseRequestInfo,
        record: validatedData,
      };
      const result = await processBulkUpload(request);

      const counts = getImportStatusCounts(result.response);
      setToastMessage(getImportToastMessage(counts));
      setShouldRefetch(result.shouldRefetchData);
      setUploadedData(result.response);
    } catch {
      setToastMessage(TOAST_MESSAGES.FILE_PROCESSING_ERROR());
    } finally {
      setUploadState(UploadState.IMPORT_COMPLETE);
    }
  };

  const handleClose = async () => {
    if (shouldRefetch) {
      await refetchRowDataList();
    }
    resetUpload();
    onClose();
  };

  const columnDefs = useMemo(
    () => getCorelationFieldsColDefs(corelationFieldProperties, schema),
    [corelationFieldProperties, schema],
  );

  const handleDownloadErrorReport = () => {
    const fileName = `${setupName}-Error-Report`
      .replace(/\s*-\s*/g, "-")
      .replace(/\s+/g, "-");
    exportMappedMergedRowsToExcel({
      rowDataList: validatedData,
      fileName: fileName,
      selected,
      schema,
      iamUserState,
    });
  };

  return (
    <FileUploadDialog
      isOpen={isOpen}
      title="Upload File"
      uploadState={uploadState}
      attachedFile={attachedFile}
      toastMessage={toastMessage}
      columnDefs={columnDefs}
      tableData={
        UploadState.VALIDATION_COMPLETE === uploadState
          ? validatedData
          : uploadedData
      }
      hasErrors={hasErrors}
      onClose={handleClose}
      onFileChange={handleFileChange}
      onFileRemove={removeFile}
      onValidate={handleValidate}
      onImport={handleImport}
      onReset={() => {
        resetUpload();
        resetFileUploadData(dispatch);
      }}
      onDownloadTemplate={async () =>
        await downloadMergedSheetUploadTemplate({ schema, setupName })
      }
      onDownloadErrorReport={handleDownloadErrorReport}
      onClearToast={() => setToastMessage(DEFAULT_TOAST)}
    />
  );
}
